import '.ap{border-radius:1.25rem;}.ssr-plugin'
import '.hi{padding:1rem 1.5rem 0 1.5rem;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.da{user-select:none;}.ssr-plugin'
import '.hj{will-change:transform;}.ssr-plugin'
import '.gl{margin-left:auto;}.ssr-plugin'
import '.ac{flex-shrink:0;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { useRef, useState } from 'react'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { Text } from '@app/components/core/typography/Text'
import Row from '@app/components/layout/Row'
import Column from '@app/components/layout/Column'
import { faHeart, faComment } from '@fortawesome/pro-light-svg-icons'
import { faHeart as faSolidHeart } from '@fortawesome/pro-solid-svg-icons'
import { faEllipsis, faShare } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PostMediaGallery } from '@app/components/blocks/mediaGallery/PostMediaGallery'
import { Tag } from '@app/components/core/tag/Tag'
import { IconButton } from '@app/components/core/button/IconButton'
import { Button } from '@app/components/core/button/Button'
import {
  DropdownMenu,
  type DropdownMenuItem,
} from '@app/components/core/dropdown/DropdownMenu'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { useTranslations } from '@i18n/useLanguage'
import { Title } from '@app/components/core/typography/Title'

export type DiscussionCardCategory = {
  id: number
  name: string
  primaryColour: string
  backgroundColour: string
  borderColour: string
}

export type DiscussionCardMediaItem = {
  src: string
  mimeType?: string
  name?: string
}

export interface DiscussionCardProps {
  authorName: string
  authorAvatar?: string
  postedIn?: string
  date: string
  updatedAt?: string
  title?: string
  content: string
  likes: number
  replies: number
  liked?: boolean
  onLikeClick?: () => void
  categories?: DiscussionCardCategory[]
  media?: DiscussionCardMediaItem[]
  onClick?: () => void
  onEdit?: () => void
  onDelete?: () => void
  shareUrl?: string
  shareTitle?: string
  style?: Style
}

const getCardBackgroundColour = (
  categories?: DiscussionCardCategory[],
): string => {
  if (categories?.length === 1) {
    return categories[0].backgroundColour
  }
  return '#FFFFFF'
}

const getCardBorder = (
  categories?: DiscussionCardCategory[],
  hovered?: boolean,
): string => {
  if (categories?.length === 1) {
    return `1px solid ${categories[0].borderColour}`
  }
  if (hovered) {
    return '1px solid #dcdaf0'
  }
  return 'var(--border-subtle)'
}

const EASE_BOUNCE = 'cubic-bezier(.34,1.56,.64,1)'
const LONG_PRESS_MS = 450

type PressState = 'rest' | 'pressed' | 'longpress'

const getCardTransform = (hovered: boolean, pressState: PressState): string => {
  if (pressState === 'longpress') return 'translateY(0) scale(0.965)'
  if (pressState === 'pressed') return 'translateY(0) scale(0.985)'
  if (hovered) return 'translateY(-2px) scale(1.006)'
  return 'translateY(0) scale(1)'
}

const getCardBoxShadow = (hovered: boolean, pressState: PressState): string => {
  if (pressState === 'longpress') return '0 2px 6px rgba(20,21,43,0.10)'
  if (pressState === 'pressed') return '0 1px 2px rgba(20,21,43,0.06)'
  if (hovered) return '0 8px 18px rgba(20,21,43,0.08)'
  return '0 3px 10px 0 rgba(0, 0, 0, 0.05)'
}

const getCardTransition = (pressState: PressState): string => {
  if (pressState === 'pressed') {
    return `transform .16s ${EASE_BOUNCE}, box-shadow .1s ease-out`
  }
  if (pressState === 'longpress') {
    return `transform .28s ${EASE_BOUNCE}, box-shadow .22s ease`
  }
  return `transform .28s ${EASE_BOUNCE}, box-shadow .2s ease, border-color .2s ease`
}

export const DiscussionCard = ({
  authorName,
  authorAvatar,
  postedIn,
  date,
  updatedAt,
  title,
  content,
  likes,
  replies,
  liked = false,
  onLikeClick,
  categories,
  media,
  onClick,
  onEdit,
  onDelete,
  shareUrl,
  shareTitle,
  style,
}: DiscussionCardProps) => {
  const t = useTranslations().app.discussions
  const tDiscussions = useTranslations().app.post
  const tCommon = useTranslations().app.common
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [pressState, setPressState] = useState<PressState>('rest')
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isInteractive = !!onClick

  const handlePointerDown = () => {
    if (!isInteractive) return
    setPressState('pressed')
    longPressTimer.current = setTimeout(() => {
      setPressState('longpress')
    }, LONG_PRESS_MS)
  }

  const handlePointerUp = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
    if (isInteractive) setPressState('rest')
  }

  const handlePointerLeave = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
    setPressState('rest')
    setHovered(false)
  }

  const handlePointerEnter = () => {
    if (isInteractive) setHovered(true)
  }

  const postedInPrefix = t.postedIn.replace('{0}', '').trimEnd()
  const backgroundColour = getCardBackgroundColour(categories)
  const border = getCardBorder(categories, hovered && pressState === 'rest')
  const images = (media ?? [])
    .filter((item) => item.mimeType !== 'application/pdf')
    .map((item) => item.src)

  const menuItems: DropdownMenuItem[] = []
  if (onEdit) {
    menuItems.push({
      id: 'edit',
      label: t.menuEdit,
      linkProps: {
        onClick: onEdit,
        textStyle: { textWrap: 'nowrap' },
      },
    })
  }
  if (onDelete) {
    menuItems.push({
      id: 'delete',
      label: t.menuDelete,
      linkProps: {
        onClick: onDelete,
        textStyle: { textWrap: 'nowrap' },
      },
    })
  }

  return (
    <styled.div className="ap hi d e g da hj"
      data-testid="discussion-card"
      onClick={onClick}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerLeave}
      onPointerEnter={handlePointerEnter}
      style={{
        backgroundColor: backgroundColour,
        border,
        cursor: onClick ? 'pointer' : 'default',
        transform: getCardTransform(hovered, pressState),
        boxShadow: getCardBoxShadow(hovered, pressState),
        transition: getCardTransition(pressState),
        ...style,
      }}
    >
      <Row gap="0.75rem" style={{ alignItems: 'flex-start' }}>
        <Avatar size="small" image={authorAvatar} labelSource={authorName} />
        <Column gap="0.25rem" style={{ flex: 1 }}>
          <Row gap="0.25rem" style={{ flexWrap: 'wrap', alignItems: 'baseline' }}>
            <Text.Medium
              isBold={true}
              style={{
                color: 'var(--brand-secondary)',
                lineHeight: '1.4',
              }}
            >
              {authorName}
            </Text.Medium>
            {postedIn && (
              <Text.Medium
                style={{
                  color: 'var(--brand-secondary)',
                  lineHeight: '1.4',
                }}
              >
                {`${postedInPrefix} `}
                <Text.Medium
                  isBold={true}
                  style={{
                    color: 'var(--brand-secondary)',
                    lineHeight: '1.4',
                  }}
                >
                  {postedIn}
                </Text.Medium>
              </Text.Medium>
            )}
          </Row>
          <Text.Small
            style={{
              color: 'var(--gray-300)',
              lineHeight: '1.4',
            }}
          >
            {date}
            {updatedAt ? ` | ${t.editedLabel} ${updatedAt}` : ''}
          </Text.Small>
        </Column>
        {menuItems.length > 0 && (
          <div className="gl ac"
            onClick={(event) => event.stopPropagation()}
          >
            <DropdownMenu items={menuItems} alignment="end">
              <IconButton
                variant="transparent"
                size="small"
                icon={faEllipsis}
                onClick={() => {}}
                style={{
                  background: 'transparent',
                  border: 'none',
                }}
              />
            </DropdownMenu>
          </div>
        )}
      </Row>

      <Column gap="0.5rem" alignItems="stretch">
        {title && (
          <Title.H5
            style={{ color: 'var(--brand-secondary)', lineHeight: '1.4' }}
          >
            {title}
          </Title.H5>
        )}

        {images.length > 0 && (
          <PostMediaGallery images={images} clickable={false} />
        )}

        <Text.Medium
          style={{
            color: 'var(--brand-secondary)',
            lineHeight: '1.75rem',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {content}
        </Text.Medium>

        {categories && categories.length > 0 && (
          <Row gap="0.5rem" style={{ flexWrap: 'wrap', marginTop: '1.5rem' }}>
            {categories.map((category) => (
              <Tag
                key={category.id}
                label={category.name}
                variant="accent"
                size="small"
                style={{
                  backgroundColor: category.primaryColour,
                  background: category.primaryColour,
                  border: `1px solid ${category.borderColour}`,
                }}
              />
            ))}
          </Row>
        )}
      </Column>

      <Row
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Row
          gap="1.5rem"
          style={{
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Row
            gap="0.5rem"
            style={{
              alignItems: 'center',
              cursor: onLikeClick ? 'pointer' : 'default',
            }}
            onClick={(event) => {
              if (!onLikeClick) {
                return
              }
              event.stopPropagation()
              onLikeClick()
            }}
          >
            <FontAwesomeIcon
              icon={liked ? faSolidHeart : faHeart}
              style={{
                color: liked
                  ? 'var(--palette-red-400)'
                  : 'var(--brand-secondary)',
                width: '1rem',
                height: '1em',
              }}
            />
            <Text.Small
              style={{
                color: 'var(--brand-secondary)',
              }}
            >
              {likes}
            </Text.Small>
          </Row>

          <Row gap="0.5rem" style={{ alignItems: 'center' }}>
            <FontAwesomeIcon
              icon={faComment}
              style={{
                color: 'var(--brand-secondary)',
                width: '1rem',
                height: '1em',
              }}
            />
            <Text.Small
              style={{
                color: 'var(--brand-secondary)',
              }}
            >
              {replies} {replies === 1 ? tDiscussions.oneReply : tDiscussions.manyReplies}
            </Text.Small>
          </Row>
        </Row>

        {shareUrl && (
          <styled.div onClick={(event) => event.stopPropagation()}>
            <Button
              variant="secondary"
              size="small"
              icon={faShare}
              iconPosition="left"
              label={tCommon.share}
              onClick={() => setShareModalOpen(true)}
            />
          </styled.div>
        )}
      </Row>

      {shareUrl && (
        <div onClick={(e) => e.stopPropagation()}>
          <SocialShareModal
            isOpen={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            url={shareUrl}
            title={shareTitle}
          />
        </div>
      )}
    </styled.div>
  )
}
